import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export interface ChangelogItem {
  title: string;
  description: string;
  depth: number;
  kanTag?: string;
}

export interface ChangelogSection {
  title: string;
  items: ChangelogItem[];
}

export interface ChangelogEntry {
  version: string;
  date: string;
  sections: ChangelogSection[];
}

@Injectable({ providedIn: 'root' })
export class AppReleaseService {
  private readonly http = inject(HttpClient);
  private readonly markdownState = signal('');
  private readonly entriesState = signal<ChangelogEntry[]>([]);
  private readonly loadedState = signal(false);
  private loadRequest: Promise<void> | null = null;

  readonly markdown = computed(() => this.markdownState());
  readonly entries = computed(() => this.entriesState());
  readonly latestEntry = computed(() => this.entriesState()[0] ?? null);
  readonly currentVersion = computed(() => this.latestEntry()?.version ?? 'v0.0.0');
  readonly currentYear = computed(() => new Date().getFullYear());
  readonly isLoaded = computed(() => this.loadedState());

  async load(): Promise<void> {
    if (this.loadedState()) return;
    if (this.loadRequest) return this.loadRequest;

    this.loadRequest = (async () => {
      try {
        const markdown = await firstValueFrom(
          this.http.get('/CHANGELOG.md', { responseType: 'text' }),
        );
        this.markdownState.set(markdown);
        this.entriesState.set(this.parseMarkdown(markdown));
      } catch (e) {
        console.error('Error in changelog load', e);
        this.markdownState.set('');
        this.entriesState.set([]);
      } finally {
        this.loadedState.set(true);
        this.loadRequest = null;
      }
    })();

    return this.loadRequest;
  }

  private parseMarkdown(markdown: string): ChangelogEntry[] {
    const lines = markdown.replace(/\r\n/g, '\n').split('\n');
    const entries: ChangelogEntry[] = [];
    let currentEntry: ChangelogEntry | null = null;
    let currentSection: ChangelogSection | null = null;

    for (const rawLine of lines) {
      const line = rawLine.trimEnd();
      const versionMatch = line.match(/^##\s+\[(.+?)\]\s+-\s+(.+)$/);
      if (versionMatch) {
        currentEntry = {
          version: versionMatch[1].trim(),
          date: versionMatch[2].trim(),
          sections: [],
        };
        entries.push(currentEntry);
        currentSection = null;
        continue;
      }

      if (!currentEntry) continue;

      const sectionMatch = line.match(/^###\s+(.+)$/);
      if (sectionMatch) {
        currentSection = {
          title: sectionMatch[1].trim(),
          items: [],
        };
        currentEntry.sections.push(currentSection);
        continue;
      }

      const itemMatch = rawLine.match(/^(\s*)-\s+(.+)$/);
      if (itemMatch && currentSection) {
        const raw = itemMatch[2].trim();
        const kanMatch = raw.match(/^\*\*KAN-(\d+)\*\*\s*[—-]\s*/);
        const body = kanMatch ? raw.slice(kanMatch[0].length) : raw;
        const colonIdx = body.indexOf(':');
        const title = colonIdx !== -1 ? body.slice(0, colonIdx).trim() : body.trim();
        const description = colonIdx !== -1 ? body.slice(colonIdx + 1).trim() : '';
        currentSection.items.push({
          title,
          description,
          depth: itemMatch[1].length > 0 ? 1 : 0,
          kanTag: kanMatch ? `KAN-${kanMatch[1]}` : undefined,
        });
      }
    }

    return entries;
  }
}
